import moment from 'moment';
import Api from "services/Api";
import store from "store/index";
import Validator from 'services/Validator';
import {requestPayload} from 'store/modules/claim';
import {areAllTrue, dateFromServer} from 'services/helper';
import {previousValid} from 'store/generators/step';
import {serviceTemplate} from 'store/modules/shared/templates';

export const mutationTypes = {
    ADD_SERVICE: 'ADD_SERVICE',
    UPDATE_SERVICE: 'UPDATE_SERVICE',
    DELETE_SERVICE: 'DELETE_SERVICE',
    LOAD_FROM_SERVER: 'LOAD_FROM_SERVER',
    ADD_SERVICE_TABLE: 'ADD_SERVICE_TABLE',
    UPDATE_SERVICE_TABLE: 'UPDATE_SERVICE_TABLE',
    UPDATE_SERVICE_TABLE_LIST: 'UPDATE_SERVICE_TABLE_LIST',
    RESET: 'RESET',
    UPDATE_SERVICE_STATUS: 'UPDATE_SERVICE_STATUS',
};

/**
 * Table Row Template
 *
 * @param serverRow
 */
const tableRowTemplate = serverRow => ({
    period: serverRow.period || null,
    duration: serverRow.duration || 0,
    multiplicand: serverRow.multiplicand || 0,
    multiplier: serverRow.multiplier || 0,
    discount: serverRow.discount || 0,
    discountedMultiplier: serverRow.discounted_multiplier || 0,
    dependencyValue: serverRow.dependency_value || 0,
});

// Initial State
const state = {
    list: [],
    tableEntries: [],
    loading: false
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {};

// Getters
const getters = {
    find: state => index => state.list[index],
    table: state => index => state.tableEntries[index],
    all: state => state.list,
    allServicesProcessed: state => state.list.reduce(
        (valid, service) => !valid ? valid : service.calculatedDependency >= 0 && service.multiplicand >= 0,
        true),
    preDependencyValue: state => state.tableEntries
        .map(row => row.pre)
        .reduce((total, row) => total + row.dependencyValue, 0),
    postDependencyValue: state => state.tableEntries
        .map(row => row.post)
        .reduce((total, row) => total + row.dependencyValue, 0),
    getServicesStatus: state => state.loading,
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param dispatch
     * @param payload
     */
    save({dispatch}, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data['services'] = state;

        Api.claims.save(payload).then((response) => {
            return response;
        });

        store.dispatch('fatal/dependency/save', requestPayload(3));
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param commit
     * @param step
     */
    load({dispatch, action, commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        commit(mutationTypes.LOAD_FROM_SERVER, data);
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({rootState}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 4,
            valid: previousValid(rootState, 4) && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({rootState}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 4,
            valid: previousValid(rootState, 4) && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({commit}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 4,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Add a service
     *
     * @param commit
     * @param dispatch
     * @param state
     */
    add({commit, dispatch, state}) {
        commit(mutationTypes.ADD_SERVICE);
        return dispatch('calcDuration', {index: state.list.length - 1});
    },

    /**
     * Recalculate the services in case the DOD has changed.
     *
     * @param dispatch
     */
    reCalcServices({dispatch}) {
        if (state.list.length) {
            state.list.forEach((service, index) => dispatch('calcDuration', {index}));
        }
    },

    /**
     * Update Discount Rate
     *
     * @param dispatch
     */
    updateTheMultiplicand({dispatch}) {
        return Promise.all([
            dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true}),
        ]);
    },

    /**
     * Determine the length of duration and update service as appropriate
     *
     * @param commit
     * @param rootGetters
     * @param state
     * @param index
     * @param key
     * @return {*}
     */
    calcDuration({commit, rootGetters, state}, {index, key}) {
        const dateOfDeath = rootGetters['fatal/dependency/deceased/theDeceased']['dateOfDeath'];
        const dateOfBirth = rootGetters['fatal/dependency/deceased/theDeceased']['dateOfBirth'];
        const mDateOfBirth = moment(dateOfBirth, 'DD/MM/YYYY');
        const durationOfDependency = rootGetters['fatal/dependency/dependants/durationOfDependency'];
        const service = state.list[index];
        const assumedSettlement = rootGetters['fatal/dependency/claimDetails/value']('assumedSettlement');
        const mAssessmentDate = moment(assumedSettlement, 'DD/MM/YYYY');
        const duration = durationOfDependency(0);
        const mDateOfDeath = moment(dateOfDeath, 'DD/MM/YYYY');
        const preTrial = mAssessmentDate.diff(mDateOfDeath, 'years', true);
        const assumedDateOfDeath = rootGetters['fatal/dependency/deceased/theDeceasedLifeExpectancy']['dateOfDeath'];
        switch (true) {
            case !service:
                return;
            case service.durationType === 'life':
                commit(mutationTypes.UPDATE_SERVICE, {key: 'duration', index, value: duration.duration + preTrial});
                commit(mutationTypes.UPDATE_SERVICE, {key: 'toDate', index, value: assumedDateOfDeath});
                break;
            case (service.durationType === 'date' && key === 'toDate'):
                const toDate = moment(service.toDate, 'DD/MM/YYYY');
                if (!toDate.isValid()) {
                    break;
                }
                commit(mutationTypes.UPDATE_SERVICE, {
                    key: 'duration',
                    index,
                    value: toDate
                        .diff(mDateOfDeath, 'years', true),
                });
                break;
            case service.durationType === 'years_and_months' && (key === 'years' || key === "months"):
                let endOfDuration = mDateOfDeath.clone().add(service.years, 'years').add(service.months, 'months');
                commit(mutationTypes.UPDATE_SERVICE, {
                    key: 'duration',
                    index,
                    value: endOfDuration.diff(mDateOfDeath, 'years', true)
                });
                commit(mutationTypes.UPDATE_SERVICE, {key: 'toDate', index, value: endOfDuration.format("DD/MM/YYYY")});
                break;
            case service.durationType === 'age' && key === 'age':
                let endOfAgeDuration = mDateOfBirth.clone().add(service.age, 'years');
                commit(mutationTypes.UPDATE_SERVICE, {
                    key: 'duration',
                    index,
                    value: endOfAgeDuration.diff(mDateOfDeath, 'years', true)
                });
                commit(mutationTypes.UPDATE_SERVICE, {
                    key: 'toDate',
                    index,
                    value: endOfAgeDuration.format("DD/MM/YYYY")
                });
                break;
            case service.durationType === 'date' && key === 'duration':
                commit(mutationTypes.UPDATE_SERVICE, {
                    key: 'toDate',
                    index,
                    value: mDateOfDeath.add(service.duration, 'years').format('DD/MM/YYYY'),
                });
            case service.durationType === 'date':
                commit(mutationTypes.UPDATE_SERVICE, {key: 'duration', index, value: service.duration});
                commit(mutationTypes.UPDATE_SERVICE, {key: 'toDate', index, value: service.toDate});
                break;
        }
    },

    /**
     * Update a service
     *
     * @param dispatch
     * @param index
     * @param service
     */
    update({dispatch}, {index, service}) {
        const proms = Object.keys(service).map(key => dispatch('updateValue', {key, index, value: service[key]}));
        return Promise.all(proms).then((updatedKeys) => {
            const proms = updatedKeys.map((key) => {
                const triggerKeys = [
                    'durationType',
                    'toDate',
                    'duration',
                    'years',
                    'months',
                    'age'
                ];
                if (~triggerKeys.indexOf(key)) {
                    return dispatch('calcDuration', {index, key});
                }
            });
            return Promise.all(proms);
        })
            .then(() => dispatch('updateValid', index))
            .then((valid) => {
                if (!valid && !isNaN(service.multiplicand)) {
                    return;
                }
                return Promise.all([
                    dispatch('fatal/dependency/updateServices', null, {root: true}),
                    dispatch('fatal/dependency/updateServicesTable', null, {root: true}),
                    dispatch('fatal/dependency/headsOfLoss/updatePastDependencyOnServices', null, {root: true}),
                ]);
            });
    },

    /**
     * Update a service value
     *
     * @param commit
     * @param state
     * @param index
     * @param key
     * @param value
     */
    updateValue({commit, state}, {index, key, value}) {
        if (state.list[index][key] !== value) {
            commit(mutationTypes.UPDATE_SERVICE, {index, key, value});
            return key;
        }
    },

    /**
     * Update values calculated form the server
     *
     * @param commit
     * @param state
     * @param index
     * @param key
     * @param value
     */
    updateFromServer({dispatch}, {index, data}) {
        return Promise.all([
            dispatch('updateValue', {index, key: 'ogdenTableE', value: data.pre_assessment_discount}),
            dispatch('updateValue', {index, key: 'ogdenTableF', value: data.post_assessment_discount}),
            dispatch('updateValue', {index, key: 'ogdenTableTwentyEight', value: data.table_28_multiplier}),
            dispatch('updateValue', {index, key: 'calculatedDependency', value: data.value}),
        ]);
    },

    /**
     * Update table values calculated form the server
     *
     * @param commit
     * @param dispatch
     * @param index
     * @param data
     */
    updateTableFromServer({commit, dispatch}, {index, data}) {
        data.forEach((row) => {
            commit(mutationTypes.UPDATE_SERVICE_TABLE, {
                index,
                tableRow: tableRowTemplate(row),
            });
        });
        dispatch('fatal/dependency/headsOfLoss/updatePastDependencyOnServices', null, {root: true});
    },

    /**
     * Update valid parts of the form
     *
     * @param state
     * @param dispatch
     * @param index
     */
    updateValid({dispatch, state}, index) {
        const service = state.list[index];
        const valid = service.duration > 0 && service.multiplicand > 0;
        dispatch('updateValue', {key: 'valid', index, value: valid});
        return valid;
    },

    /**
     * Delete a service
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param index
     */
    deleteService({commit, state, dispatch}, index) {
        commit(mutationTypes.DELETE_SERVICE, index);
        if (index === 0) {
            dispatch('add');
        }
    },

    /**
     * Update the service status
     *
     * @param commit
     * @param state
     */
    updateServiceStatus({commit}) {
        commit(mutationTypes.UPDATE_SERVICE_STATUS);
    },
};

// Mutations
const mutations = {
    [mutationTypes.ADD_SERVICE](state) {
        state.list.push(serviceTemplate());
    },
    [mutationTypes.UPDATE_SERVICE](state, {key, value, index}) {
        state.list[index][key] = value;
    },
    [mutationTypes.UPDATE_SERVICE_TABLE](state, {index, tableRow}) {
        if (!state.tableEntries[index]) {
            state.tableEntries.push({
                pre: {},
                post: {},
            });
        }
        state.tableEntries[index][tableRow.period] = tableRow;
    },
    [mutationTypes.UPDATE_SERVICE_TABLE_LIST](state, payload) {
        state.tableEntries = payload;
    },
    [mutationTypes.DELETE_SERVICE](state, index) {
        state.list.splice(index, 1);
        state.tableEntries.splice(index, 1);
    },
    [mutationTypes.LOAD_FROM_SERVER](state, data) {
        state.list = data.list;
        state.tableEntries = data.tableEntries;
    },
    [mutationTypes.RESET](state) {
        state.list = [];
        state.tableEntries = [];
    },
    [mutationTypes.UPDATE_SERVICE_STATUS](state) {
        state.loading = !state.loading;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/dependency/dependencyOnServices.js